/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2025-01-15 11:44:53
 **/

import debounce from "lodash-es/debounce";
import { produce } from "immer";
import useDeviceManager from "./useDeviceManager";
import { PermissionEventCallback } from "./permissionmanager";

type DeviceManagerOptions = Partial<{
	permission_checking: boolean;
	request_permission: boolean;
	switch_new_device: boolean;
}>;

function is_identical_device(newDevice: DeviceType.MediaDeviceSetting, oldDevice: DeviceType.MediaDeviceSetting) {
	if (oldDevice.id) {
		return newDevice.id === oldDevice.id;
	}
	function to_crude_id(device: DeviceType.MediaDeviceSetting) {
		return `${device.kind}:${device.group_id}`;
	}

	return to_crude_id(newDevice) === to_crude_id(oldDevice);
}

const LOGTAG = "#DeviceManager#";

export default abstract class DeviceManagerBase {
	#_detect_timeout = 1400;
	#_currentDevices: DeviceType.MediaDeviceSetting[] = [];
	#_changedDevices: DeviceType.DeviceChange[] = [];
	#_default_audio_devices = new Map<string, DeviceType.MediaDeviceSetting | null | undefined>();
	#_last_error: Error | null = null;
	#_options: DeviceManagerOptions = {
		permission_checking: true,
		request_permission: false,
		switch_new_device: false
	};
	#_use_debugging = false;

	protected constructor() {}

	public get use_debugging() {
		return this.#_use_debugging;
	}

	public set use_debugging(value: boolean) {
		this.#_use_debugging = value;
	}

	/**
	 * 현재 사용 가능한 미디어 장치 설정의 목록을 반환합니다.
	 *
	 * @return {Array<DeviceType.MediaDeviceSetting>} 미디어 장치 설정의 배열을 반환합니다.
	 */
	public get devices(): Array<DeviceType.MediaDeviceSetting> {
		return useDeviceManager.getState().devices;
	}

	protected get currentDevices(): DeviceType.MediaDeviceSetting[] {
		return this.#_currentDevices;
	}

	protected set currentDevices(value: DeviceType.MediaDeviceSetting[]) {
		this.#_currentDevices = value;
	}

	protected get changedDevices() {
		return this.#_changedDevices;
	}

	protected set changedDevices(value: DeviceType.DeviceChange[]) {
		this.#_changedDevices = value;
	}

	/**
	 * 이 메서드는 기본 오디오 장치를 나타내는 맵을 반환합니다.
	 *
	 * @return {Map<string, DeviceType.MediaDeviceSetting | null | undefined>} 기본 오디오 장치에 대한 맵
	 */
	public get default_audio_devices(): Map<string, DeviceType.MediaDeviceSetting | null | undefined> {
		return this.#_default_audio_devices;
	}

	/**
	 * `detect_timeout` 속성의 getter 메서드입니다.
	 * 이 메서드는 `#_detect_timeout` 속성의 값을 반환합니다.
	 *
	 * @return {number} `detect_timeout` 값. 이 값은 감지 시간 초과 값을 나타냅니다.
	 */
	public get detect_timeout(): number {
		return this.#_detect_timeout;
	}

	/**
	 * detect_timeout 값을 설정합니다.
	 *
	 * @param {number} value 타임아웃 값을 나타내는 숫자입니다.
	 */
	public set detect_timeout(value: number) {
		this.#_detect_timeout = value;
	}

	/**
	 * 최근 발생한 오류를 반환합니다.
	 *
	 * @return {Error | null} 마지막으로 발생한 오류 객체. 오류가 없을 경우 null을 반환합니다.
	 */
	public get last_error(): Error | null {
		return this.#_last_error;
	}

	/**
	 * 마지막 오류를 설정합니다.
	 *
	 * @param {Error | null} value 설정할 오류 객체입니다. null을 설정하는 경우 오류 상태를 초기화합니다.
	 */
	protected set last_error(value: Error | null) {
		this.#_last_error = value;
	}

	/**
	 * DeviceManagerOptions 객체를 반환합니다.
	 *
	 * @return {DeviceManagerOptions} 현재 설정된 DeviceManagerOptions 객체
	 */
	public get options(): DeviceManagerOptions {
		return this.#_options;
	}

	/**
	 * 장치 관리 옵션을 설정합니다.
	 *
	 * @param {DeviceManagerOptions} options 병합하여 설정할 옵션 객체
	 * @return {void} 반환값 없음
	 */
	public set_options(options: DeviceManagerOptions) {
		this.#_options = { ...this.#_options, ...options };
	}

	/**
	 * 이 메서드는 초기화를 수행합니다.
	 * 초기화 작업이 완료되면 Promise가 반환됩니다.
	 *
	 * @return {Promise<void>} 초기화 작업 완료를 나타내는 Promise
	 */
	public abstract initialize(): Promise<void>;
	#re_initialize = debounce(this.initialize, this.detect_timeout);
	protected install_event_devicechange() {
		if (!navigator.mediaDevices.ondevicechange) {
			navigator.mediaDevices.ondevicechange = async () => {
				await this.#re_initialize();
			};
			this.use_debugging && console.log(LOGTAG, "install_event_devicechange");
		}
	}

	/**
	 * 모든 디바이스를 초기 상태로 리셋합니다.
	 * @return {Promise<void>} 초기화 작업이 완료되었을 때 해결되는 Promise 객체
	 */
	public abstract reset_devices(): Promise<void>;

	/**
	 * 사용자의 장치 권한을 확인하고 필요에 따라 권한을 요청합니다.
	 *
	 * @param {boolean} request_permission 권한 요청 플래그. true일 경우 권한이 "prompt" 상태일 때 필요한 장치 권한을 요청합니다.
	 * @param {PermissionEventCallback | null} [event_callback] 권한 요청 전에 추가 작업을 수행하거나 요청 후 작업을 처리하기 위한 콜백. 선택적으로 사용할 수 있습니다.
	 * @return {Promise<PermissionStatus[]>} 사용자의 장치 권한 상태 배열을 반환합니다.
	 */
	public abstract checking_device_permission(
		request_permission: boolean,
		event_callback?: PermissionEventCallback | null
	): Promise<PermissionStatus[]>;

	protected check_device_changes(newDevices: DeviceType.MediaDeviceSetting[]) {
		const oldDevices = this.devices;
		useDeviceManager.getState().set_devices(newDevices);
		this.use_debugging && console.log(LOGTAG, "check_device_changes", this.devices, newDevices);
		const changedDevices = this.#calculate_device_changes(newDevices, oldDevices);
		if (changedDevices.length) {
			this.#_changedDevices = this.#_changedDevices.concat(changedDevices);
			this.#_currentDevices = newDevices.map(this.#normalize_device_info);
			this.#current_devices_detection(this.#_currentDevices, this.#_changedDevices);
			this.#_changedDevices = [];
			this.#_currentDevices = [];
		} else {
			this.#current_devices_detection(this.#_currentDevices, this.#_changedDevices);
		}
	}

	/**
	 * 주어진 장치 종류 및 ID에 해당하는 장치를 반환합니다.
	 *
	 * @param {DeviceType.AllDeviceKinds} kind 검색할 장치의 종류(audioinput, audiooutput 등)
	 * @param {string} device_id 검색할 장치의 고유 ID
	 * @return {DeviceType.MediaDeviceSetting} 검색된 장치 객체. 해당 장치가 없을 경우 빈 객체를 반환합니다.
	 */

	public abstract get_device_with_id(
		kind: DeviceType.AllDeviceKinds,
		device_id: string
	): DeviceType.MediaDeviceSetting;

	protected async enumerateDevices() {
		const { camera, microphone, speaker } = useDeviceManager.getState();
		const raw_devices = await navigator.mediaDevices.enumerateDevices();
		const devices = raw_devices
			.filter((device) => device.deviceId.length > 0)
			.reduce<DeviceType.MediaDeviceSetting[]>((total, current) => {
				const device_info = (() => {
					if (current.deviceId.length <= 0) {
						switch (current.kind) {
							case "audioinput":
								return microphone;
							case "audiooutput":
								return speaker;
							case "videoinput":
								return camera;
						}
					}
					return {
						id: current.deviceId,
						group_id: current.groupId,
						kind: current.kind,
						name: current.label,
						permission: "granted"
					} as DeviceType.MediaDeviceSetting;
				})();

				total.push(device_info);
				return total;
			}, []);
		this.default_audio_devices.set("audiooutput", this.get_default_audio_device(devices, "audiooutput"));
		this.default_audio_devices.set("audioinput", this.get_default_audio_device(devices, "audioinput"));
		const result = devices.filter(
			(device) => device.id !== "default" && device.id !== "communications" && device.id !== ""
		);
		if (this.use_debugging) {
			console.log(
				LOGTAG,
				"default audio devices:\n",
				"audio output",
				this.default_audio_devices.get("audiooutput"),
				"audio input",
				this.default_audio_devices.get("audioinput"),
				result
			);
		}
		return result;
	}

	#normalize_device_info(device: DeviceType.MediaDeviceSetting) {
		return {
			id: device.id || "",
			group_id: device.group_id || "",
			kind: device.kind,
			name: device.name || ""
		} as DeviceType.MediaDeviceSetting;
	}

	#calculate_device_changes(
		newDevices: DeviceType.MediaDeviceSetting[],
		oldDevices: DeviceType.MediaDeviceSetting[]
	) {
		const removals = oldDevices.slice();
		const updates: DeviceType.DeviceChange[] = [];

		const additions = newDevices.filter((newDevice) => {
			const oldDeviceIndex = removals.findIndex((oldDevice) => is_identical_device(newDevice, oldDevice));
			if (oldDeviceIndex > -1) {
				const [oldDevice] = removals.splice(oldDeviceIndex, 1);

				if (newDevice.name !== oldDevice.name) {
					const update: DeviceType.DeviceUpdateEvent = {
						type: "update",
						newInfo: newDevice,
						oldInfo: oldDevice
					};

					updates.push(update);
				}
			}

			return oldDeviceIndex === -1;
		});
		return [
			...updates,

			// A device was just removed.
			...removals.map((device) => {
				return {
					type: "remove",
					device
				} as DeviceType.DeviceRemoveEvent;
			}),

			// A device was just plugged in.
			...additions.map((device) => {
				return { type: "add", device } as DeviceType.DeviceAddEvent;
			})
		];
	}

	protected get_default_audio_device(devices: DeviceType.MediaDeviceSetting[], kind: "audioinput" | "audiooutput") {
		const default_device = devices.filter((device) => device.id === "default" && device.kind === kind)?.[0] ?? null;
		if (default_device) {
			return devices.filter(
				(device) =>
					device.group_id === default_device.group_id && device.kind === kind && device.id !== "default"
			)?.[0];
		}
		return null;
	}

	#current_devices_detection(
		currentDevices: DeviceType.MediaDeviceSetting[],
		changedDevices: DeviceType.DeviceChange[]
	) {
		useDeviceManager.setState((state) =>
			produce(state, (draft) => {
				const camera_id = draft.camera.id ?? "";
				const microphone_id = draft.microphone.id ?? "";
				const speaker_id = draft.speaker.id ?? "";
				const is_init_device =
					(camera_id.length <= 0 && microphone_id.length <= 0 && speaker_id.length <= 0) ||
					changedDevices.filter((device) => device.type === "add").length > 2;

				this.use_debugging &&
					console.log(
						LOGTAG,
						"current_devices_detection",
						camera_id,
						microphone_id,
						speaker_id,
						is_init_device,
						changedDevices
					);

				if (currentDevices.length >= 0) {
					if (this.options.switch_new_device && !is_init_device) {
						for (const changed_device of changedDevices) {
							switch (changed_device.type) {
								case "add": {
									const name =
										changed_device.device.kind === "videoinput"
											? "camera"
											: changed_device.device.kind === "audioinput"
												? "microphone"
												: changed_device.device.kind === "audiooutput"
													? "speaker"
													: null;

									if (name) {
										draft[name] = {
											...draft[name],
											...changed_device.device
										};
									}
									break;
								}

								case "remove": {
									const name =
										changed_device.device.kind === "videoinput"
											? "camera"
											: changed_device.device.kind === "audioinput"
												? "microphone"
												: changed_device.device.kind === "audiooutput"
													? "speaker"
													: null;

									if (name && changed_device.device.kind) {
										draft[name] = {
											...draft[name],
											...this.get_device_with_id(changed_device.device.kind, "")
										};
									}
									break;
								}
							}
						}
					} else {
						if (currentDevices.filter((device) => device.id === camera_id).length === 0) {
							draft.camera = {
								...draft.camera,
								...this.get_device_with_id("videoinput", camera_id)
							};
						}

						if (currentDevices.filter((device) => device.id === microphone_id).length === 0) {
							draft.microphone = {
								...draft.microphone,
								...this.get_device_with_id("audioinput", microphone_id)
							};
						}

						if (currentDevices.filter((device) => device.id === speaker_id).length === 0) {
							draft.speaker = {
								...draft.speaker,
								...this.get_device_with_id("audiooutput", speaker_id)
							};
						}
					}
				}
			})
		);
	}
}
